/**
 * Phase 5-B — AdsumInlineTrigger.
 *
 * Replaces the Phase 4-b floating Adsum orb. Rendered INSIDE SchedulePanel,
 * between the date/time + platforms stack and the "Mark as Ready" CTA.
 *
 *  - Non-Pro: locked upsell card → opens SubscriptionDrawer, does NOT call
 *             /api/agent3/adsum/review.
 *  - Pro:     mint-bordered row with Run Review (−5 NXT) button that opens
 *             the existing AdsumDrawer (all drawer internals — EN/ES toggle,
 *             score ring, Apply tweak — remain unchanged).
 *
 *  Visual state mirrors the orb's score/phase so the user still gets the
 *  compact brand-score chip once a review completes.
 */

import { motion } from "framer-motion";
import { Crown, Loader2, Lock, ShieldCheck } from "lucide-react";

import { scoreColor } from "@/lib/adsum-colors";
import { useSession } from "@/contexts/SessionContext";

export default function AdsumInlineTrigger({
    onOpen,
    phase,
    review,
    disabled,
}) {
    const { tier, openSubscriptionDrawer } = useSession();
    const locked = tier !== "pro";
    const score = phase === "result" && review?.brand_score;
    const badgeColor = typeof score === "number" ? scoreColor(score) : null;
    const isLoading = phase === "loading";

    // ------------------------------------------------------------------
    // Locked state — passive upsell card. NEVER fires an Adsum API call.
    // ------------------------------------------------------------------
    if (locked) {
        return (
            <motion.div
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="relative overflow-hidden rounded-xl border border-dashed border-[#1F2A40] bg-[#111726] p-4"
                data-testid="adsum-inline-trigger"
                data-locked="true"
            >
                <div
                    aria-hidden
                    className="pointer-events-none absolute inset-0 opacity-70"
                    style={{
                        background:
                            "radial-gradient(circle at 90% 10%, rgba(0,245,212,0.08) 0%, transparent 65%)",
                    }}
                />
                <div className="relative flex flex-wrap items-center gap-3 sm:flex-nowrap">
                    <div
                        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-[#1F2A40] bg-[#0B0F19] text-[#00F5D4]"
                        style={{
                            boxShadow:
                                "0 0 14px rgba(0,245,212,0.18), inset 0 0 0 1px rgba(0,245,212,0.3)",
                        }}
                        data-testid="adsum-inline-lock-icon"
                    >
                        <Lock className="h-4 w-4" strokeWidth={2.2} />
                    </div>
                    <div className="flex flex-1 flex-col gap-0.5">
                        <div className="flex items-center gap-2">
                            <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#8A93A6]">
                                adsum qc review · pro
                            </span>
                            <span className="inline-flex items-center gap-1 rounded-full border border-[#00F5D4]/40 bg-[#00F5D4]/10 px-1.5 py-0.5 font-mono text-[8px] uppercase tracking-[0.24em] text-[#00F5D4]">
                                <Crown className="h-2.5 w-2.5" strokeWidth={2.5} />
                                pro
                            </span>
                        </div>
                        <p className="text-sm text-[#E8ECF5]">
                            Audit title, caption & schedule before you commit.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={() =>
                            openSubscriptionDrawer({
                                code: "tier_required",
                                required: "pro",
                                feature: "Adsum QC Review",
                            })
                        }
                        data-testid="adsum-inline-unlock-btn"
                        className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-gradient-to-r from-[#2F58CD] to-[#2F58CD] px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-white transition-all hover:shadow-[0_0_18px_rgba(0,245,212,0.4)]"
                    >
                        <Crown className="h-3 w-3" />
                        Unlock with Pro
                    </button>
                </div>
            </motion.div>
        );
    }

    // ------------------------------------------------------------------
    // Pro state — tappable row, opens AdsumDrawer.
    // ------------------------------------------------------------------
    return (
        <motion.button
            type="button"
            onClick={() => !disabled && onOpen?.()}
            disabled={disabled}
            data-testid="adsum-inline-trigger"
            data-locked="false"
            data-phase={phase || "idle"}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className={[
                "group relative flex w-full flex-wrap items-center gap-3 overflow-hidden rounded-xl border bg-[#111726] p-4 text-left transition-all sm:flex-nowrap",
                disabled
                    ? "cursor-not-allowed border-[#1F2A40] opacity-60"
                    : "border-[#00F5D4]/35 hover:border-[#00F5D4] hover:shadow-[0_0_22px_rgba(0,245,212,0.22)]",
            ].join(" ")}
        >
            <div
                aria-hidden
                className="pointer-events-none absolute inset-0 opacity-60 transition-opacity group-hover:opacity-100"
                style={{
                    background:
                        "radial-gradient(circle at 85% 20%, rgba(0,245,212,0.1) 0%, transparent 70%)",
                }}
            />
            <div
                className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-[#00F5D4]/50 bg-[#0B0F19] text-[#00F5D4]"
                style={{
                    boxShadow:
                        "0 0 12px rgba(0,245,212,0.25), inset 0 0 0 1px rgba(0,245,212,0.35)",
                }}
            >
                {isLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                    <ShieldCheck className="h-4 w-4" strokeWidth={1.8} />
                )}
                {typeof score === "number" && !isLoading && (
                    <span
                        className="absolute -top-1 -right-1 inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full border border-[#0B0F19] bg-[#0B0F19] px-1 font-mono text-[9px] font-semibold tabular-nums"
                        style={{
                            color: badgeColor,
                            boxShadow: `0 0 10px ${badgeColor}, inset 0 0 0 1px ${badgeColor}`,
                        }}
                        data-testid="adsum-inline-score-badge"
                        data-score={score}
                    >
                        {score}
                    </span>
                )}
            </div>
            <div className="relative flex flex-1 flex-col gap-0.5">
                <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#00F5D4]">
                    pre-publish qc
                </span>
                <span className="text-sm text-[#E8ECF5]">
                    Audit title, caption & schedule.
                </span>
            </div>
            <span className="relative inline-flex shrink-0 items-center gap-1.5 rounded-md border border-[#1F2A40] bg-[#0B0F19] px-3 py-1.5 text-xs font-medium text-[#E8ECF5] transition-colors group-hover:border-[#00F5D4]/60 group-hover:text-[#00F5D4]">
                Run Review
                <span className="font-mono text-[10px] text-[#8A93A6]">
                    −5 NXT
                </span>
            </span>
        </motion.button>
    );
}
